/**
 * Shared prompt snippets that teach agents about the
 * `docs/project-context.md` convention file.
 *
 * The file is the canonical answer to "what is this project about?" —
 * mission, domain vocabulary, in/out-of-scope capabilities, and key
 * stakeholders. Two variants are exported:
 *
 * - {@link PROJECT_CONTEXT_READER_SECTION} — for execution agents that
 *   read the file for framing but never edit it.
 * - {@link PROJECT_CONTEXT_MAINTAINER_SECTION} — for learning agents
 *   (meeting-analyst, requirements-analyst) that seed the file on first
 *   use and update it when new project-scope facts emerge.
 *
 * Each export is a `ReadonlyArray<string>` so it can be spread directly
 * into a sub-agent `prompt` array.
 */

/** Path to the convention file, relative to the repo root. */
export const PROJECT_CONTEXT_PATH = "docs/src/content/docs/project-context.md";

/**
 * Shared guidance on deriving a sub-project's role from the top-level
 * segment of its `outdir`. Included in both the reader and maintainer
 * sections so every agent understands the convention without needing
 * an external link. Kept self-contained — agents never need to follow
 * a cross-reference to interpret the rule.
 */
const SUBPROJECT_ROLE_GUIDANCE: ReadonlyArray<string> = [
  "### Sub-project roles",
  "",
  "When the repository follows the configulator monorepo layout,",
  "every sub-project's role is derivable from the first segment of",
  "its `outdir` path. No other lookup is required.",
  "",
  "- `apps/<scope>/<name>` — deployable application (CDK stack,",
  "  mobile app, backend service).",
  "- `packages/<scope>/<name>` — shared library (published npm",
  "  package or workspace-internal library).",
  "- `sites/<scope>/<name>` — user-facing web front end that is",
  "  not the monorepo-wide docs site.",
  "- `docs/` — the single Starlight documentation site for the",
  "  whole monorepo (exactly one per repo; lives at `/docs`, not",
  "  under `sites/`).",
  "",
  "Repositories that have not adopted the layout contract may use a",
  "different folder structure — in that case fall back to whatever",
  `\`${PROJECT_CONTEXT_PATH}\` documents explicitly.`,
  "",
];

/**
 * Read-only framing section. Spread into prompts for agents that should
 * consult `docs/project-context.md` but never edit it.
 */
export const PROJECT_CONTEXT_READER_SECTION: ReadonlyArray<string> = [
  "## Project Context",
  "",
  `Before doing any work, read \`${PROJECT_CONTEXT_PATH}\` at the`,
  'repository root. It is the canonical answer to "what is this project',
  'about?" — mission, domain vocabulary, in/out-of-scope capabilities, and',
  "key stakeholders. Use it to frame judgment calls in this session.",
  "",
  `If \`${PROJECT_CONTEXT_PATH}\` does not exist, proceed with the current`,
  "task and note the absence in your session log — the meeting-analyst and",
  "requirements-analyst agents seed the file on their next run.",
  "",
  "You are a **read-only consumer** of this file. Do not edit it.",
  "",
  ...SUBPROJECT_ROLE_GUIDANCE,
  "---",
  "",
];

/**
 * Maintainer section. Spread into prompts for agents that own updates
 * to `docs/project-context.md`. Includes the seed template and the
 * trigger conditions for updates.
 *
 * The seed template embeds a `{{monorepoLayoutSeedBlock}}` template
 * variable. When the consuming monorepo has layout enforcement on
 * (`layoutEnforcement !== "off"`), `ProjectMetadata` resolves it to a
 * grouped sub-project list skeleton (Applications / Packages / Sites /
 * Docs) so the seeded file surfaces each sub-project's role up front.
 * Repos with layout enforcement off resolve it to an empty string, so
 * the seed template is unchanged.
 */
export const PROJECT_CONTEXT_MAINTAINER_SECTION: ReadonlyArray<string> = [
  "## Project Context",
  "",
  `Before starting any phase, read \`${PROJECT_CONTEXT_PATH}\` at the`,
  'repository root. It is the canonical answer to "what is this project',
  'about?" — mission, domain vocabulary, in/out-of-scope capabilities, and',
  "key stakeholders. Use it to judge relevance when scanning source",
  "material in this session.",
  "",
  ...SUBPROJECT_ROLE_GUIDANCE,
  "### Seed on first use",
  "",
  `If \`${PROJECT_CONTEXT_PATH}\` does not exist, create it from this`,
  "template and commit it alongside this phase's other outputs:",
  "",
  "```markdown",
  "# Project Context",
  "",
  "> Canonical description of this project. Read by all agents before",
  "> work. Updated by meeting-analyst and requirements-analyst when new",
  "> scope, vocabulary, or stakeholders emerge.",
  "",
  "## Mission",
  "TODO: one or two sentences on what this project exists to do.",
  "",
  "## Target Users",
  "TODO: who uses this, in what role.",
  "",
  "## In-Scope Capabilities",
  "TODO: bullet list of the capabilities this project owns.",
  "",
  "## Out-of-Scope",
  "TODO: capabilities or concerns that are explicitly not this project's.",
  "",
  "## Domain Vocabulary",
  "TODO: short glossary of domain terms, acronyms, and their definitions.",
  "",
  "## Key Stakeholders",
  "TODO: named people or teams and what they care about.",
  "{{monorepoLayoutSeedBlock}}",
  "## References",
  "TODO: links to BCM docs, competitive analysis, product roadmap, and",
  "other authoritative sources.",
  "```",
  "",
  "Fill whatever you can infer from the source material you are already",
  "reading in this phase; leave `TODO:` placeholders for the rest.",
  "",
  "### Update on new facts",
  "",
  "When the source material you process reveals new project-scope",
  "information — a new capability, a vocabulary term, an entity, a",
  "stakeholder, an in/out-of-scope decision, or a shift in mission —",
  `append or revise the relevant section in \`${PROJECT_CONTEXT_PATH}\``,
  "before closing the phase. Commit those edits with the phase's other",
  "outputs.",
  "",
  "Keep edits surgical: short bullet additions, brief clarifications, or",
  "single-line vocabulary entries. Treat the file as an accreting",
  "reference, not a document you reshape every session.",
  "",
  "---",
  "",
];

/**
 * Markdown block appended to the maintainer seed template when the
 * consuming monorepo has layout enforcement on. Groups sub-projects
 * by role (Applications / Packages / Sites / Docs) so the seeded
 * `docs/project-context.md` surfaces each sub-project's role from
 * day one. The heading sits between "Key Stakeholders" and
 * "References" so the seeded document still reads top-to-bottom when
 * the block is absent.
 *
 * Returned as a plain string (with embedded newlines) so it can drop
 * straight into `resolveTemplateVariables` as the resolved value for
 * `{{monorepoLayoutSeedBlock}}`. The leading newline ensures the
 * block's `##` heading starts on a fresh line when it replaces a
 * whole template-variable line.
 */
export const MONOREPO_LAYOUT_SEED_BLOCK: string = [
  "",
  "## Sub-Projects",
  "",
  "TODO: one bullet per sub-project, grouped by role. The role is",
  "derivable from the first segment of each sub-project's `outdir`.",
  "",
  "### Applications (`apps/`)",
  "TODO: deployable applications (CDK stacks, mobile apps, services).",
  "",
  "### Packages (`packages/`)",
  "TODO: shared libraries (published npm packages, workspace-internal libraries).",
  "",
  "### Sites (`sites/`)",
  "TODO: user-facing web front ends (marketing sites, SPAs, client apps).",
  "",
  "### Docs (`docs/`)",
  "TODO: the single monorepo-wide Starlight documentation site.",
  "",
].join("\n");
